import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/addons/postprocessing/OutlinePass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { RENDER } from '../config.js';

// A quiet vignette plus a whisper of grain, to stop the sky from looking like
// a flat fill on large screens.
const GradeShader = {
    uniforms: {
        tDiffuse: { value: null },
        uAmount: { value: 0.42 },
        uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: /* glsl */ `
        uniform sampler2D tDiffuse;
        uniform float uAmount;
        uniform float uTime;
        varying vec2 vUv;

        void main() {
            vec4 c = texture2D(tDiffuse, vUv);
            vec2 p = vUv - 0.5;
            float vig = 1.0 - dot(p, p) * uAmount;
            c.rgb *= vig;
            float grain = fract(sin(dot(vUv * (1.0 + uTime * 0.0001), vec2(12.9898, 78.233))) * 43758.5453);
            c.rgb += (grain - 0.5) * 0.012;
            gl_FragColor = c;
        }
    `,
};

export class Engine {
    constructor(canvas) {
        this.canvas = canvas;

        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: false,          // MSAA happens in the composer target
            powerPreference: 'high-performance',
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, RENDER.maxPixelRatio));
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        // Every pass of the composer resets these counters, so left to itself
        // renderer.info ends the frame describing the last fullscreen quad —
        // two triangles — rather than the scene. Reset once a frame instead,
        // and the numbers add up across the passes.
        this.renderer.info.autoReset = false;
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.05;

        this.scene = new THREE.Scene();

        this.camera = new THREE.PerspectiveCamera(
            48,
            window.innerWidth / window.innerHeight,
            0.5,
            900,
        );
        this.camera.position.set(-34, 26, 40);

        this.controls = new OrbitControls(this.camera, canvas);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.06;
        this.controls.minDistance = 12;
        this.controls.maxDistance = 105;
        this.controls.maxPolarAngle = Math.PI / 2 - 0.06;   // never go below the water
        this.controls.minPolarAngle = 0.18;
        this.controls.enablePan = false;
        this.controls.target.set(0, 3, 0);
        this.controls.autoRotateSpeed = 0.32;

        this._setupComposer();

        this._clock = new THREE.Clock();
        this._resize = this._resize.bind(this);
        window.addEventListener('resize', this._resize);

        // Idle → slow orbit. Any input stops it for a while.
        this._idleTimer = 0;
        this._idleDelay = 6;
        for (const ev of ['pointerdown', 'wheel', 'keydown', 'touchstart']) {
            window.addEventListener(ev, () => this.wake(), { passive: true });
        }

        this._flight = null;
    }

    _setupComposer() {
        const size = this.renderer.getDrawingBufferSize(new THREE.Vector2());
        const target = new THREE.WebGLRenderTarget(size.x, size.y, {
            type: THREE.HalfFloatType,
            samples: 4,
        });

        this.composer = new EffectComposer(this.renderer, target);
        this.composer.addPass(new RenderPass(this.scene, this.camera));

        this.outline = new OutlinePass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            this.scene,
            this.camera,
        );
        this.outline.edgeStrength = 3.4;
        this.outline.edgeGlow = 0.35;
        this.outline.edgeThickness = 1.4;
        this.outline.pulsePeriod = 0;
        this.outline.visibleEdgeColor.set('#fff4d6');
        this.outline.hiddenEdgeColor.set('#8b7a4f');
        this.outline.selectedObjects = [];
        this.composer.addPass(this.outline);

        // Bloom, thresholded high so only the emissive windows and lanterns
        // cross it. Strength is driven by the clock — see setNightAmount.
        this.bloom = new UnrealBloomPass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            0.0,    // strength, animated
            0.75,   // radius
            1.05,   // threshold
        );
        this.composer.addPass(this.bloom);

        this.grade = new ShaderPass(GradeShader);
        this.composer.addPass(this.grade);

        this.composer.addPass(new OutputPass());
    }

    _resize() {
        const w = window.innerWidth;
        const h = window.innerHeight;
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, RENDER.maxPixelRatio));
        this.renderer.setSize(w, h);
        this.composer.setSize(w, h);
        this.outline.setSize(w, h);
        this.bloom.setSize(w, h);
    }

    wake() {
        this._idleTimer = 0;
        this.controls.autoRotate = false;
    }

    /** Bloom belongs to the night; at noon it would just haze the sky. */
    setNightAmount(night) {
        this.bloom.strength = 0.05 + night * 0.42;
    }

    /**
     * Glide the camera to look at a point from a pleasant angle.
     * @param {THREE.Vector3} target
     * @param {number} distance
     */
    flyTo(target, distance = 26, duration = 1.5) {
        const from = {
            pos: this.camera.position.clone(),
            target: this.controls.target.clone(),
        };

        // Approach from the sunward side so the subject is lit, not silhouetted.
        const dir = new THREE.Vector3()
            .subVectors(this.camera.position, this.controls.target)
            .setY(0)
            .normalize();
        if (dir.lengthSq() < 0.001) dir.set(0, 0, 1);
        const to = {
            target: target.clone(),
            pos: target.clone().add(dir.multiplyScalar(distance)).setY(target.y + distance * 0.52),
        };

        this._flight = { from, to, t: 0, duration };
        this.wake();
    }

    /** True while a flyTo is in progress, so nothing else moves the camera. */
    get isFlying() {
        return Boolean(this._flight);
    }

    _updateFlight(dt) {
        if (!this._flight) return;
        const f = this._flight;
        f.t += dt / f.duration;
        const t = Math.min(1, f.t);
        // Smootherstep: no visible acceleration snap at either end.
        const e = t * t * t * (t * (t * 6 - 15) + 10);
        this.camera.position.lerpVectors(f.from.pos, f.to.pos, e);
        this.controls.target.lerpVectors(f.from.target, f.to.target, e);
        if (t >= 1) this._flight = null;
    }

    /** @returns {number} delta time in seconds */
    beginFrame() {
        this.renderer.info.reset();
        const dt = Math.min(this._clock.getDelta(), 0.05);
        this._idleTimer += dt;
        if (this._idleTimer > this._idleDelay && !this._flight) {
            this.controls.autoRotate = true;
        }
        this._updateFlight(dt);
        this.controls.update();
        return dt;
    }

    render() {
        this.composer.render();
    }

    dispose() {
        window.removeEventListener('resize', this._resize);
        this.controls.dispose();
        this.renderer.dispose();
    }
}
